// components/Sidebar.tsx
import React from "react";
import {
  Activity,
  LayoutGrid,
  User,
  UserCircle,
  Bell,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  BookUser,
  SquareKanban,
  Info,
} from "lucide-react";

import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectSidebarExpanded, toggleSidebar } from "../../store/sidebarSlice";
import SupportyLogo from "../../assets/auth/supporty-logo.svg";
import SupportIcon from "../../assets/icons/grey_suppoty.svg";
import teamsIcon from "../../assets/icons/teamIcon.svg";
import tooltipIcon from "../../assets/icons/tooltipIcon.svg";
import { useAppSelector } from "../../store/hooks";
import {
  selectUserNotifications,
  selectAllNotifications,
} from "../../store/slices/notificationSlice";

import LogoutIcon from "../../assets/auth/logout.png";
import { SidebarItem } from "../../types";
import { logout } from "../../store/authSlice";
import { AppDispatch } from "../../store"; // Import AppDispatch type
import { useAppState } from "../../hooks/useAppState";

const Sidebar: React.FC = () => {
  const isExpanded = useSelector(selectSidebarExpanded);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppState();
  console.log(user, "user in sidebar");
  // Get notifications from Redux state
  const userNotifications = useAppSelector(selectUserNotifications);
  const allNotifications = useAppSelector(selectAllNotifications);

  // Display appropriate notifications based on user role
  const displayedNotifications =
    user?.role === "supporty_admin" ? allNotifications : userNotifications;
  const getSidebarItems = (): SidebarItem[] => {
    if (!user) return [];

    if (user?.role === "supporty_admin") {
      return [
        {
          icon: <LayoutGrid className="w-[24px] h-[24px]" />,
          label: "Dashboard",
          path: "/admin/dashboard",
        },
        {
          icon: <SquareKanban className="w-[24px] h-[24px]" />,
          label: "Activity",
          path: "/admin/activity",
        },
        {
          icon: <BookUser className="w-[24px] h-[24px]" />,
          label: "Directory",
          path: "/admin/directory",
        },
        {
          icon: <img src={teamsIcon} className="w-[24px] h-[24px]"/>,
          label: "Teams",
          path: "/admin/teams",
        },
        { icon:  <img src={tooltipIcon} className="w-[24px] h-[24px]"/>, label: "Tooltips", path: "/admin/tooltip" },

        { icon: <User className="w-[24px] h-[24px]" />, label: "Profile", path: "/admin/profile" },
        // {
        //   icon: <CreditCard className="w-[24px] h-[24px]" />,
        //   label: "Payment",
        //   path: "/admin/payment",
        // },

        {
          icon: <Bell className="w-[24px] h-[24px]" />,
          label: "Notification",
          path: "/admin/notification",
        },
        {
          icon: <Settings className="w-[24px] h-[24px]" />,
          label: "Settings",
          path: "/admin/setting",
        },
      ];
    }

    if (user?.role === "Expert" || user?.role === "expert_owner") {
      return [
        {
          icon: <LayoutGrid className="w-[24px] h-[24px]" />,
          label: "Dashboard",
          path: "/expert/dashboard",
        },
        {
          icon: <SquareKanban className="w-[24px] h-[24px]" />,
          label: "My Activity",
          path: "/expert/activity",
        },
        // Only show Teams if not expert_team_member
        user?.teamRole !== "expert_team_member"
          ? {
              icon: <img src={teamsIcon}  className="w-[24px] h-[24px]  " />,
              label: "Teams",
              path: "/expert/teams",
            }
          : null,
        { icon: <img src={tooltipIcon} className="w-[24px] h-[24px]"/>, label: "Tooltips", path: "/expert/tooltips" },
        // { icon: <Info className="w-[24px] h-[24px]" />, label: "Tooltip", path: "/expert/tooltip" },
        { icon: <User className="w-[24px] h-[24px]" />, label: "Profile", path: "/expert/profile" },
        {
          icon: <Bell className="w-[24px] h-[24px]"/>,
          label: "Notification",
          path: "/expert/notification",
        },
        {
          icon: <Settings className="w-[24px] h-[24px]" />,
          label: "Settings",
          path: "/expert/setting",
        },
      ].filter(Boolean);
    }

    if (user?.role === "Customer") {
      return [
        {
          icon: <LayoutGrid className="w-[24px] h-[24px]" />,
          label: "Dashboard",
          path: "/user/dashboard",
        },
        {
          icon: <SquareKanban className="w-[24px] h-[24px]" />,
          label: "My Activity",
          path: "/user/activity",
        },
        // Only show Teams if not expert_team_member
        user?.teamRole !== "customer_team_member"
          ? {
              icon: <img src={teamsIcon} className="w-[24px] h-[24px]" />,
              label: "Teams",
              path: "/user/teams",
            }
          : null,

        {
          icon:  <img src={tooltipIcon} className="w-[24px] h-[24px]"/>,
          label: "Tooltips",
          path: "/user/tooltip",
        },
        user?.teamRole === "customer_owner"
          ? {
              icon: <CreditCard className="w-[24px] h-[24px]" />,
              label: "Payment",
              path: "/user/payment",
            }
          : null,
        {
          icon: <User className="w-[24px] h-[24px]" />,
          label: "Profile",
          path: "/user/profile",
        },
        {
          icon: <Bell className="w-[24px] h-[24px]" />,
          label: "Notification",
          path: "/user/notification",
        },
        {
          icon: <Settings className="w-[24px] h-[24px]" />,
          label: "Settings",
          path: "/user/setting",
        },
      ].filter(Boolean);
    }

    return [];
  };

  const sidebarItems = getSidebarItems();

  const handleNavigation = (path: string): void => {
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap(); // Use unwrap() to handle the Promise
      // Clear any local storage or session data if needed
      localStorage.clear();
      sessionStorage.clear();
      // Redirect to signin page and refresh the page for complete state reset
      navigate("/signin", { replace: true });
      // window.location.reload();
    } catch (error) {
      console.error("Error logging out:", error);
      // Even if logout fails, redirect to signin page for security
      navigate("/signin", { replace: true });
      // window.location.reload();
    }
  };

  return (
    <div
      className={`bg-[#2C3F54] text-white h-screen fixed left-0 top-0 transition-all duration-300 ${
        isExpanded ? "w-60" : "w-20"
      }`}
    >
      <div className="flex items-center justify-between p-4 px-8">
        <div className="text-2xl font-bold">
          <img
            src={SupportyLogo}
            alt="Supporty.io Logo"
            className={`mx-auto h-14 w-auto ${isExpanded ? "ml-2" : "ml-0"}`}
          />
        </div>
        <button
          onClick={() => dispatch(toggleSidebar())}
          className="p-1 hover:bg-slate-700 rounded"
        >
          {isExpanded ? <ChevronLeft className="w-[24px] h-[24px]" /> : <ChevronRight className="w-[24px] h-[24px]" />}
        </button>
      </div>

      <nav className="mt-8">
        {sidebarItems.map((item, index) => (
          <div
            key={index}
            onClick={() => handleNavigation(item.path)}
            className={`flex items-center gap-4 ${
              isExpanded ? "px-3 mx-8" : "px-2 mx-2 justify-center"
            } py-3 rounded-xl my-2 cursor-pointer hover:bg-[#F15D18] ${
              location.pathname === item.path ? "bg-[#F15D18]" : ""
            }`}
          >
            {" "}
            <div className="relative">
              {item.icon}
              {item.label === "Notification" &&
                (() => {
                  const unreadCount = displayedNotifications.filter((n) => !n.read)
                    .length;
                  return (
                    unreadCount > 0 && (
                      <span className="absolute -top-2 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                       <span className="text-[9px]">
                         {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                      </span>
                    )
                  );
                })()}
            </div>
            {isExpanded && <span className="ml-2">{item.label}</span>}
          </div>
        ))}
      </nav>

      <div
        className={`absolute bottom-4 ${isExpanded ? "w-60 px-1" : "w-20 px-"}`}
      >
        <div
          className={`relative z-10 flex items-center text-gray-300 hover:text-white ${
            isExpanded ? "px-3 mx-8" : "px-2 mx-2 justify-center"
          } py-3 rounded-xl my-2 cursor-pointer hover:bg-[#F15D18] transition-colors duration-200`}
          onClick={handleLogout}
        >
          <img src={LogoutIcon} className="w-[24px] h-[24px]" alt="Logout" />
          {isExpanded && <span className="ml-3 text-white">Logout</span>}
        </div>
        <div className="mt-2">
          <img
            src={SupportIcon}
            alt="Supporty Icon"
            className={`absolute ${isExpanded ? "bottom-[0px]" : "bottom-6"} ${
              isExpanded ? "right-8" : "right-0"
            } w-24 h-40`}
          />
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
